import React from "react";
import {
  Bath,
  BedDouble,
  Coffee,
  DoorOpen,
  Dumbbell,
  Refrigerator,
  Snowflake,
  Sparkles,
  Tv,
  Waves,
  Wifi,
  type LucideIcon,
} from "lucide-react";

export const DEFAULT_ROOM_FEATURES = [
  "Wi-Fi",
  "TV",
  "Air conditioning",
  "Balcony",
  "Fridge",
] as const;

export const DEFAULT_CUSTOM_FEATURE_ICON = "sparkles";

export const DEFAULT_ROOM_FEATURE_ICONS: Record<string, string> = {
  "Wi-Fi": "wifi",
  TV: "tv",
  "Air conditioning": "snowflake",
  Balcony: "door-open",
  Fridge: "refrigerator",
};

export const ROOM_FEATURE_ICON_OPTIONS: Array<{
  value: string;
  label: string;
  icon: LucideIcon;
}> = [
  { value: "wifi", label: "Wi-Fi", icon: Wifi },
  { value: "tv", label: "TV", icon: Tv },
  { value: "snowflake", label: "Air conditioning", icon: Snowflake },
  { value: "door-open", label: "Balcony", icon: DoorOpen },
  { value: "refrigerator", label: "Fridge", icon: Refrigerator },
  { value: "bed", label: "Bed", icon: BedDouble },
  { value: "bath", label: "Bathroom", icon: Bath },
  { value: "coffee", label: "Coffee", icon: Coffee },
  { value: "waves", label: "Pool / spa", icon: Waves },
  { value: "dumbbell", label: "Fitness", icon: Dumbbell },
  { value: DEFAULT_CUSTOM_FEATURE_ICON, label: "Custom", icon: Sparkles },
];

const ICONS_BY_VALUE = new Map(
  ROOM_FEATURE_ICON_OPTIONS.map((option) => [option.value, option.icon]),
);

export function normalizeFeatureName(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function uniqueFeatureNames(features: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const feature of features) {
    const normalized = normalizeFeatureName(feature);

    if (normalized && !seen.has(normalized.toLowerCase())) {
      seen.add(normalized.toLowerCase());
      unique.push(normalized);
    }
  }

  return unique;
}

export function getDefaultFeatureIcon(feature: string): string {
  return DEFAULT_ROOM_FEATURE_ICONS[feature] ?? DEFAULT_CUSTOM_FEATURE_ICON;
}

export function pickAmenityIcons(
  amenities: string[],
  amenityIcons?: Record<string, string>,
): Record<string, string> {
  return amenities.reduce<Record<string, string>>((acc, amenity) => {
    const icon = amenityIcons?.[amenity] ?? getDefaultFeatureIcon(amenity);

    if (icon) {
      acc[amenity] = icon;
    }

    return acc;
  }, {});
}

export function RoomFeatureIcon({
  icon,
  className = "h-4 w-4 text-muted-foreground",
}: {
  icon?: string;
  className?: string;
}) {
  const Icon = ICONS_BY_VALUE.get(icon ?? "") ?? Sparkles;

  return <Icon className={className} aria-hidden />;
}
